import { API_FUNDING_PROGRAM } from "globals/constants";
import { buildSearchParams, request } from "utils/proxy";

/**
 * Short Funding Program Round Data Object
 *
 * @typedef {Object} ShortFundingProgramRound
 *
 * @property {string} id - The unique ID of the funding program round.
 * @property {string} name - The name of the funding program round.
 * @property {number} year - The year associated with the funding program round.
 */

/**
 * Funding Program Round Data Object
 *
 * @typedef {Object} FundingProgramRound
 *
 * @property {string} roundId - The unique ID of the funding program round.
 * @property {string} name - The name of the funding program round.
 * @property {string} notes - Additional notes or description for the funding program round.
 * @property {number} year - The year associated with the funding program round.
 * @property {boolean} active - Indicates whether the funding program round is active (true) or inactive (false).
 * @property {string} internalClosingDate - The internal closing date and time for the funding program round.
 * @property {string} externalClosingDate - The external closing date and time for the funding program round.
 * @property {string} fundingProgramId - The ID of the associated funding program.
 * @property {string} tenantId - The ID of the tenant.
 * @property {string} adminUpdateDate - The date and time when this funding program round was last updated.
 *
 */

/**
 *
 * @template {object} T
 *
 * @typedef {Object<T>} ListResponse
 *
 * @property {T[]} content - The list of short funding program objects.
 * @property {number} totalElements - The total number of elements.
 * @property {number} totalPages - The total number of pages.
 * @property {boolean} last - Indicates whether this is the last page.
 * @property {boolean} first - Indicates whether this is the first page.
 * @property {number} number - The current page number.
 * @property {number} size - The number of items per page.
 * @property {number} numberOfElements - The number of elements on this page.
 * @property {boolean} empty - Indicates whether the list is empty.
 * @property {object} pageable - The pageable object.
 * @property {object} sort - The sort object.
 * @property {boolean} sort.sorted - Indicates whether the list is sorted.
 * @property {boolean} sort.empty - Indicates whether the list is empty.
 * @property {boolean} sort.unsorted - Indicates whether the list is unsorted.
 */

/**
 * A collection of functions for interacting with funding program rounds.
 * @namespace
 */
export const fundingProgramRounds = {
	/**
	 * Retrieve a list of funding program rounds.
	 *
	 * @param {string} programId - The ID of the funding program.
	 * @param {object} [queryParams] - Query parameters for filtering and pagination.
	 * @param {number} [queryParams.page] - The page number for pagination.
	 * @param {number} [queryParams.size] - The number of items per page.
	 * @param {string[]} [queryParams.search] - An array of search terms for filtering.
	 *
	 * @returns {Promise<ListResponse<ShortFundingProgramRound>>} A Promise that resolves with the list of funding program rounds.
	 */
	findAll: (programId, queryParams) => {
		return request(
			`${API_FUNDING_PROGRAM}/${programId}/funding-round${buildSearchParams(queryParams)}`,
		);
	},

	/**
	 * Retrieve information about a funding program round.
	 *
	 * @param {string} programId - The ID of the funding program.
	 * @param {string} id - The ID of the round.
	 *
	 * @returns {Promise<FundingProgramRound>} A Promise that resolves with the funding program round details.
	 */
	findById: (programId, id) => {
		return request(`${API_FUNDING_PROGRAM}/${programId}/funding-round/${id}`);
	},
};
